import React from 'react';
import clsx from 'clsx';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { Link } from 'react-router-dom';
import type { WidgetChart, WidgetData } from '@app-types';
import { assessmentsListPath, hasNoData } from '@utils/widgetData';
import { WIDGET_SERIES_COLORS, seriesColor } from '@utils/widgetColors';
import styles from './WidgetDataView.module.css';

const CHART_HEIGHT = 260;

interface WidgetDataViewProps {
  data: WidgetData;
  /** Prefix of the data-test-id of the parts, so several widgets on one page stay distinguishable. */
  testIdPrefix: string;
  /** Called with the dimension the user picks; without it the view shows no switch. */
  onDimensionChange?: (dimension: string) => void;
  /** Called with the period the user picks; without it the view shows no period switch. */
  onPeriodChange?: (period: string) => void;
  /** Say "No data in this period." when there is nothing to show; the older widgets stay silent. */
  showNoDataNotice?: boolean;
}

/** One row per category with one key per series, the shape recharts reads. */
function toRows(chart: WidgetChart): Array<Record<string, string | number>> {
  return chart.categories.map((category, index) => {
    const row: Record<string, string | number> = { category };
    chart.series.forEach((series) => {
      row[series.name] = series.values[index] ?? 0;
    });
    return row;
  });
}

const ChartView: React.FC<{ chart: WidgetChart }> = ({ chart }) => {
  if (chart.categories.length === 0) {
    return null;
  }

  if (chart.type === 'pie') {
    const slices = chart.categories.map((category, index) => ({
      name: category,
      value: chart.series[0]?.values[index] ?? 0
    }));

    return (
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <PieChart>
          <Pie data={slices} dataKey="value" nameKey="name" isAnimationActive={false}>
            {slices.map((slice, index) => (
              <Cell key={slice.name} fill={seriesColor(index)} />
            ))}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  const rows = toRows(chart);
  // recharts finds the grid, axes, tooltip and legend among the direct children of the chart, and does not look
  // inside a Fragment, so they are handed over as a keyed array that React flattens into direct children.
  const axes = [
    <CartesianGrid key="grid" strokeDasharray="3 3" vertical={false} />,
    <XAxis key="x" dataKey="category" fontSize={12} />,
    <YAxis key="y" allowDecimals={false} fontSize={12} />,
    <Tooltip key="tooltip" />,
    <Legend key="legend" />
  ];

  if (chart.type === 'line') {
    return (
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <LineChart data={rows} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
          {axes}
          {chart.series.map((series, index) => (
            <Line
              key={series.name}
              type="monotone"
              dataKey={series.name}
              stroke={seriesColor(index)}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
      <BarChart data={rows} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
        {axes}
        {chart.series.map((series, index) => (
          <Bar
            key={series.name}
            dataKey={series.name}
            fill={seriesColor(index)}
            stackId={chart.type === 'stacked-bar' ? 'stack' : undefined}
            isAnimationActive={false}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

interface ChoiceSwitchProps {
  /** What the choices are, in the test ids: `dimension` or `period`. */
  kind: 'dimension' | 'period';
  /** What the group is called for assistive technology. */
  label: string;
  choices: string[];
  selected: string | undefined;
  onChange: (choice: string) => void;
  testIdPrefix: string;
}

/** The choices of a widget, one at a time: its breakdowns or its periods. */
const ChoiceSwitch: React.FC<ChoiceSwitchProps> = ({ kind, label, choices, selected, onChange, testIdPrefix }) => (
  // A printout shows the widget as it is, not the controls for changing it.
  <div
    className={clsx(styles.dimensions, styles.noPrint)}
    role="group"
    aria-label={label}
    data-test-id={`${testIdPrefix}-${kind}s`}
  >
    {choices.map((choice) => (
      <button
        key={choice}
        type="button"
        className={clsx(styles.dimension, choice === selected && styles.dimensionSelected)}
        aria-pressed={choice === selected}
        onClick={() => choice !== selected && onChange(choice)}
        data-test-id={`${testIdPrefix}-${kind}-${choice}`}
      >
        {choice}
      </button>
    ))}
  </div>
);

/**
 * What a Dashboard Widget shows: its KPI numbers, its chart and its table, all drawn from one data shape. The
 * Widget Subscription PDF draws the same shape on the server (ADR 0008), so the two look equivalent.
 */
const WidgetDataView: React.FC<WidgetDataViewProps> = ({
  data,
  testIdPrefix,
  onDimensionChange,
  onPeriodChange,
  showNoDataNotice = false
}) => (
  <div className={styles.view} data-test-id={`${testIdPrefix}-data`}>
    {onPeriodChange && data.periods && data.periods.length > 0 && (
      <ChoiceSwitch
        kind="period"
        label="Period"
        choices={data.periods}
        selected={data.selected_period}
        onChange={onPeriodChange}
        testIdPrefix={testIdPrefix}
      />
    )}

    {onDimensionChange && data.dimensions && data.dimensions.length > 0 && (
      <ChoiceSwitch
        kind="dimension"
        label="Break down by"
        choices={data.dimensions}
        selected={data.selected_dimension}
        onChange={onDimensionChange}
        testIdPrefix={testIdPrefix}
      />
    )}

    {showNoDataNotice && hasNoData(data) && (
      <p className={styles.noData} role="status" data-test-id={`${testIdPrefix}-no-data`}>
        No data in this period.
      </p>
    )}

    {data.metrics.length > 0 && (
      <ul className={styles.metrics} aria-label="Key numbers">
        {data.metrics.map((metric, index) => (
          <li key={metric.label} className={styles.metric}>
            <span className={styles.metricLabel}>
              <span
                className={styles.swatch}
                style={{ backgroundColor: WIDGET_SERIES_COLORS[index % WIDGET_SERIES_COLORS.length] }}
                aria-hidden="true"
              />
              {metric.label}
            </span>
            <strong className={styles.metricValue} data-test-id={`${testIdPrefix}-metric-${index}`}>
              {metric.value}
            </strong>
          </li>
        ))}
      </ul>
    )}

    {data.chart && (
      <div data-test-id={`${testIdPrefix}-chart`} data-chart-type={data.chart.type}>
        <ChartView chart={data.chart} />
      </div>
    )}

    {data.table && data.table.columns.length > 0 && (
      <div className={styles.tableWrapper}>
        <table className={styles.table} data-test-id={`${testIdPrefix}-table`}>
          <thead>
            <tr>
              {data.table.columns.map((column) => (
                <th key={column}>{column}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.table.rows.map((row, rowIndex) => (
              // Rows have no identity of their own; the table is read-only, so the index is a safe key.
              // eslint-disable-next-line react/no-array-index-key
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => {
                  // A row is drilled into through its first cell: a table row cannot itself be a link.
                  const drillDown = cellIndex === 0 ? data.table?.drill_down?.[rowIndex] : null;
                  return (
                    // eslint-disable-next-line react/no-array-index-key
                    <td key={cellIndex}>
                      {drillDown ? (
                        <Link to={assessmentsListPath(drillDown)} className={styles.drillDown}>
                          {cell}
                        </Link>
                      ) : (
                        cell
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {data.note && (
      <p className={styles.note} data-test-id={`${testIdPrefix}-note`}>
        {data.note}
      </p>
    )}
  </div>
);

export { WidgetDataView };
